/**
 * quarantineRelease.js — the user releasing the phishing email from quarantine, and the
 * other accounts the flagged sender also delivered mail to.
 *
 * ORIGIN. On IR-20260902-tthomason-001 the single most consequential user action in the
 * case — the user pulling the lure back out of quarantine, and reporting it to Microsoft
 * as a false positive while they did it — rendered as:
 *
 *     tthomason@cmacommunities.com performed Release-QuarantineMessage
 *
 * and was ABSENT from every report. Two separate defects sat on top of each other:
 *
 *   1. Nothing correlated the release to the message it released, so the row could not
 *      say which message, from whom, or that it was the one the analyst had flagged.
 *   2. The row's only address is an INFERRED Microsoft IP (2603:10b6:5:2bd::16, medium
 *      confidence, paired from an exchange event) which is classified safe — so the
 *      Safe-IP pill dropped it, and the report's canonical filter state has every pill
 *      engaged. Even a perfectly-worded row could never have reached a report.
 *
 * 🔴 WHY THE CORRELATION HAS TO BE EXACT. This case holds SIX release events in THREE
 * pairs, and only the first pair releases the flagged message. The other two release
 * messages that have no trace row in the case at all. A sender/subject fallback, or a
 * "some quarantine release happened" heuristic, would extend the visibility exception
 * (and the report's claim) to releases the analyst never marked anything about. So the
 * join is on the NetworkMessageId and nothing else, and an unparseable release is not
 * flagged-phish evidence.
 *
 * 🔴 DEPENDENCY-FREE ON PURPOSE. `maskSubject` lives in eventUtils.js, which imports THIS
 * module for `describeQuarantineRelease` — so importing it back would cycle. The masker is
 * passed in as an argument, exactly as `describeMessageTrace` takes it.
 *
 * Every shape below was read off the real run blob, not assumed:
 *   - `RawLog` is an OBJECT on general events (not a JSON string), but the parser accepts
 *     a string too, because other workloads differ.
 *   - `Release-QuarantineMessage` (Workload SecurityComplianceCenter, RecordType 18)
 *     carries the id ONLY inside `RawLog.Parameters`, as the FIRST GUID of
 *     `-Identity "<nmid>\<something>"`. Its `RawLog.ObjectId` is an empty string.
 *   - `QuarantineReleaseMessage` (Workload Quarantine, RecordType 65) carries
 *     `RawLog.NetworkMessageId` directly.
 *   - The flagged message-trace row carries a top-level `SilverRecordId` equal to its own
 *     `RawLog.id` and to the release event's `NetworkMessageId`. That is the join key.
 *   - Delivery statuses are LOWERCASE in the blob (`quarantined`), so every comparison
 *     here is case-insensitive or it counts zero.
 */

/**
 * The two operations that ARE a release.
 *
 * 🔴 Deliberately NOT in this set, all four measured as siblings in the same case:
 * `Get-QuarantineMessage`, `Preview-QuarantineMessageV1`, `QuarantinePreviewMessage` and
 * `UserSubmission` — looking at a quarantined message is not releasing it. Nor is
 * `QuarantineRequestReleaseMessage`, which is a REQUEST for an admin to release: a
 * different claim, and asserting a release from it would be false.
 */
export const QUARANTINE_RELEASE_OPS = new Set([
  'Release-QuarantineMessage',
  'QuarantineReleaseMessage',
]);

/**
 * Days of padding on each side of the malicious-activity bounds for the
 * "other accounts to review" scope (operator, 2026-09-02).
 */
export const RELATED_WINDOW_PAD_DAYS = 5;

/** Scope modes for the related-message selection. The renderers caption on these. */
export const RELATED_SCOPE_PAD = 'malicious_activity_pad';
export const RELATED_SCOPE_CASE = 'case_range';

const GUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

const norm = (v) => String(v == null ? '' : v).trim().toLowerCase();

/** `RawLog` as an object, whether it arrived as one or as a JSON string. */
function rawLogOf(evt) {
  const raw = evt?.RawLog;
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch { return null; }
  }
  return null;
}

/** True when this event's Operation is one of the two release operations. */
export function isQuarantineReleaseOp(op) {
  return QUARANTINE_RELEASE_OPS.has(String(op || '').trim());
}

/** The cmdlet-argument text a SecurityComplianceCenter record carries, or ''. */
function paramText(raw) {
  const params = raw?.Parameters;
  if (typeof params === 'string') return params;
  if (params) { try { return JSON.stringify(params); } catch { return ''; } }
  return '';
}

/**
 * The NetworkMessageId the release acted on, or '' when it cannot be determined.
 *
 * '' is load-bearing: a release we cannot identify is not evidence about the flagged
 * message, so it must not be treated as any.
 */
export function releasedNetworkMessageId(evt) {
  if (!isQuarantineReleaseOp(evt?.Operation)) return '';
  const raw = rawLogOf(evt);
  if (!raw) return '';
  // RecordType 65 (Quarantine workload) — named directly.
  const direct = String(raw.NetworkMessageId || '').trim();
  if (direct) return direct;
  // RecordType 18 (SecurityComplianceCenter cmdlet) — first GUID of -Identity "…".
  // `Parameters` is a STRING of cmdlet arguments here, not the {Name,Value} array the
  // Exchange workload uses, so this is a text parse rather than parseParameters().
  const text = paramText(raw);
  if (!text) return '';
  const identity = /-Identity\s+"([^"]*)"/i.exec(text);
  const candidate = identity ? identity[1] : '';
  const m = GUID_RE.exec(candidate);
  return m ? m[0] : '';
}

/** Did the user also report the message to Microsoft as a false positive? */
export function releaseReportedFalsePositive(evt) {
  const raw = rawLogOf(evt);
  if (!raw) return false;
  const text = paramText(raw);
  if (!text) return false;
  return /-ReportFalsePositive\s+"?true"?/i.test(text);
}

/**
 * Map of `SilverRecordId -> { sender, subject, eventId }` for the message-trace rows the
 * analyst FLAGGED as phishing.
 *
 * 🔴 Reads the RAW MessageTraceEvents. A filtered set can have dropped the flagged row
 * (its own IP may be classified safe), and then the index is empty and the release row
 * silently loses its correlation — the exact class of failure this whole module exists
 * to remove.
 */
export function flaggedPhishMessageIndex(phishingFlags, rawMessageTraceEvents) {
  const index = new Map();
  const flags = Array.isArray(phishingFlags) ? phishingFlags : [];
  if (flags.length === 0) return index;
  const byEventId = new Map();
  for (const evt of (Array.isArray(rawMessageTraceEvents) ? rawMessageTraceEvents : [])) {
    const id = String(evt?.EventId || '').trim();
    if (id) byEventId.set(id, evt);
  }
  for (const flag of flags) {
    // Whole-string match on the composite `<silver-id>|<recipient>` EventId — message
    // trace is stored at PER-RECIPIENT grain, so splitting it would match another
    // recipient's row for the same message.
    const eventId = String(flag?.eventId || '').trim();
    if (!eventId) continue;
    const row = byEventId.get(eventId);
    // The silver id is what the release event names. Fall back to the EventId prefix
    // only when the row is present but pre-dates the top-level projection of
    // SilverRecordId (v1.152.0) — never invent one from the flag alone, which carries
    // no id at all.
    const silverId = String(row?.SilverRecordId || row?.RawLog?.id || '').trim()
      || (row ? String(row.EventId || '').split('|')[0].trim() : '');
    if (!silverId) continue;
    index.set(silverId, {
      eventId,
      sender: String(flag?.senderAddress || row?.SenderAddress || '').trim(),
      subject: String(flag?.subject || row?.Subject || '').trim(),
      // The RFC InternetMessageId -- the second exact key for "is this the same message"
      // (relatedIsSameMessage). Opaque, never rendered.
      internetMessageId: String(row?.MessageId || '').trim(),
    });
  }
  return index;
}

/** Is this event the release of a message the analyst flagged as the phish? */
export function isFlaggedPhishRelease(evt, index) {
  if (!index || index.size === 0) return false;
  const nmid = releasedNetworkMessageId(evt);
  return !!nmid && index.has(nmid);
}

/**
 * Map of `NetworkMessageId -> { timeUtc, reportedFalsePositive, eventIds }` over every
 * release event in the case.
 *
 * 🔴 DEDUPED BY NMID, because ONE release is logged as a PAIR of audit events ~3 s apart
 * (the cmdlet and the quarantine-service record). A raw event count would say the user
 * released the message twice. The earliest time wins and `reportedFalsePositive` is ORed
 * across the pair, since only the cmdlet record carries that flag.
 */
export function collectReleaseEvidence(rawGeneralEvents) {
  const out = new Map();
  for (const evt of (Array.isArray(rawGeneralEvents) ? rawGeneralEvents : [])) {
    const nmid = releasedNetworkMessageId(evt);
    if (!nmid) continue;
    const time = String(evt?.EventTimeUTC || '').trim();
    const fp = releaseReportedFalsePositive(evt);
    const eventId = String(evt?.EventId || '').trim();
    const prior = out.get(nmid);
    if (!prior) {
      out.set(nmid, {
        timeUtc: time,
        reportedFalsePositive: fp,
        eventIds: eventId ? [eventId] : [],
      });
      continue;
    }
    if (time && (!prior.timeUtc || time < prior.timeUtc)) prior.timeUtc = time;
    prior.reportedFalsePositive = prior.reportedFalsePositive || fp;
    if (eventId && !prior.eventIds.includes(eventId)) prior.eventIds.push(eventId);
  }
  return out;
}

/**
 * The operator's scope basis for "other accounts to review": earliest to latest
 * SUCCESSFUL activity from analyst-flagged malicious IPs, across BOTH the interactive
 * and the non-interactive feed. `null` when no malicious IP is classified, or when the
 * window is indeterminate — and a null basis is a DIFFERENT rendering, never a silent
 * widening (see selectRelatedMessages).
 *
 * 🔴 IT READS THE EXISTING WINDOW RATHER THAN RE-DERIVING IT. `calculateAttackerWindow`
 * already computes exactly this pair as `firstAttackerActivity` / `lastAttackerActivity`,
 * with subtleties that took several releases to get right: success-only, dismissed rows
 * excluded, security-alert and risk-detection timestamps excluded (they are DETECTION
 * times, not activity), and BOTH ENDS of every non-interactive bucket (v1.172.0 — a row
 * is an aggregate over (session, IP, hour), so its start alone stops the span up to an
 * hour short). A second copy of that in this module would drift from the case's own
 * Attacker Window panel, and then the report's scope claim and the screen would disagree.
 *
 * 🔴 DELIBERATELY NOT `globalCutoff`. That is token-expiry-EXTENDED — a modelled endpoint,
 * not observed activity — and the operator specified last ACTIVITY.
 */
export function maliciousActivityBounds(attackerWindow) {
  const aw = attackerWindow;
  if (!aw || aw.isIndeterminate) return null;
  const startUtc = String(aw.firstAttackerActivity || '').trim();
  const endUtc = String(aw.lastAttackerActivity || '').trim();
  if (!startUtc || !endUtc) return null;
  return { startUtc, endUtc };
}

function shiftIsoDays(iso, days) {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  return new Date(t + days * 86400000).toISOString();
}

/**
 * The related-message rows to show, and the scope mode that describes them.
 *
 * ONE selection helper, shared by the Executive Brief's fact pack AND the Client Report,
 * so the two cannot select different populations and print different counts.
 *
 * Order matters and is not arbitrary:
 *
 *  1. 🔴 LIVE FLAGS FIRST. `relatedPhishingMessages` was produced by the runbook using
 *     the flags that existed WHEN THE INVESTIGATION RAN, and the rows stay in the blob
 *     after the analyst removes a flag. The Executive Brief has a server reconcile pass
 *     to drop them; the Client Report has none. So rows whose `FlaggedSenderAddress` is
 *     not in the live flags' sender set are dropped here, before anything is counted,
 *     capped, or used to decide whether the section exists. An empty live-flag set
 *     yields an empty result — no section at all.
 *  2. WINDOW. With bounds, `[start - pad, end + pad]`; WITHOUT bounds, no time filter at
 *     all — and the returned `scope` says which, so a caption can state the truth. A
 *     ±5-day claim over an unwindowed set would be a false scope statement about the
 *     data the reader is looking at.
 *  3. CHRONOLOGICAL, so the first N rows of a truncated list are the earliest, which is
 *     the order an investigator reads a delivery sequence in.
 */
export function selectRelatedMessages(relatedMessages, liveFlags, bounds, padDays = RELATED_WINDOW_PAD_DAYS) {
  const rows = Array.isArray(relatedMessages) ? relatedMessages.filter(Boolean) : [];
  const flags = Array.isArray(liveFlags) ? liveFlags : [];
  const senders = new Set();
  for (const f of flags) {
    const s = norm(f?.senderAddress);
    if (s) senders.add(s);
  }
  if (senders.size === 0) return { rows: [], scope: RELATED_SCOPE_CASE };

  let kept = rows.filter((r) => senders.has(norm(r?.FlaggedSenderAddress)));

  let scope = RELATED_SCOPE_CASE;
  if (bounds?.startUtc && bounds?.endUtc) {
    const lo = shiftIsoDays(bounds.startUtc, -Math.abs(padDays));
    const hi = shiftIsoDays(bounds.endUtc, Math.abs(padDays));
    if (lo && hi) {
      scope = RELATED_SCOPE_PAD;
      const loMs = Date.parse(lo);
      const hiMs = Date.parse(hi);
      kept = kept.filter((r) => {
        const t = String(r?.EventTimeUTC || '').trim();
        if (!t) return false;          // an undatable row cannot be placed in the window
        const ms = Date.parse(t);
        if (Number.isNaN(ms)) return false;
        return ms >= loMs && ms <= hiMs;
      });
    }
  }

  kept.sort((a, b) => String(a?.EventTimeUTC || '').localeCompare(String(b?.EventTimeUTC || '')));
  return { rows: kept, scope };
}

/**
 * The related-phishing rows off a results object, whichever case the key is in.
 *
 * 🔴 IT IS `RelatedPhishingMessages` ON THE CLIENT AND `relatedPhishingMessages` IN THE
 * BLOB, and getting that wrong is invisible: `selectRelatedMessages` simply returns no
 * rows, the section is absent, and an absent optional section reads as "this case has no
 * related recipients" — a clean, confident false absence. It cost exactly that during
 * development, caught only by building a pack from the REAL case and noticing the block
 * was missing when the case's own Related Recipients tab shows two.
 *
 * `normalizeResults` (api/client.js) projects the blob's camelCase array to PascalCase, so
 * a fact pack and the Client Report see the PascalCase name while the SERVER reads the raw
 * blob's camelCase one. Both are accepted here, in one place, rather than at each call
 * site — client.js's own reader does the same for the same reason.
 */
export function relatedPhishingRows(results) {
  const rows = results?.RelatedPhishingMessages ?? results?.relatedPhishingMessages;
  return Array.isArray(rows) ? rows : [];
}

/**
 * Did this related recipient receive THE SAME MESSAGE as the flagged email?
 *
 * 🔴 AN EXACT ID MATCH, NEVER SENDER + SUBJECT + TIME. The Related Recipients query is
 * sender-wide, so its rows are "mail from that sender" until an id says otherwise. Two keys
 * qualify, both assigned by Microsoft to ONE message regardless of how many recipients it has:
 * the NetworkMessageId (the trace row's silver id) and the RFC InternetMessageId. On
 * IR-20261013-levig-001 all four other recipients carry the flagged copy's NetworkMessageId
 * and InternetMessageId -- one message sent to five people -- and the reports said "the records
 * do not establish that they received the same message", which was false. On
 * IR-20260902-tthomason-001 the other recipients' message has the same sender and subject but
 * DIFFERENT ids, and there the old sentence is still right. Same rule on the server
 * (Invoke-ExecReportReconcile), which re-derives it from the pinned blob.
 */
export function relatedIsSameMessage(row, flaggedIndex) {
  if (!row || !flaggedIndex || flaggedIndex.size === 0) return false;
  for (const id of [row?.NetworkMessageId, relatedMessageSilverId(row)]) {
    if (norm(id) && [...flaggedIndex.keys()].some((k) => norm(k) === norm(id))) return true;
  }
  const imid = norm(row?.MessageId);
  if (!imid) return false;
  for (const info of flaggedIndex.values()) {
    if (norm(info?.internetMessageId) === imid) return true;
  }
  return false;
}

/**
 * This related recipient's own clicks on links in THAT message, earliest first.
 *
 * The runbook joins url_click rows to the related message by NetworkMessageId and to the
 * recipient by AccountUpn (Step 3.9.1), so every entry is an exact match -- no proximity. A
 * click by ANOTHER person on the lure is the most urgent lead in the section, and it was
 * shown in the case's Related Recipients tab but in no report.
 */
export function relatedRecipientClicks(row) {
  // A one-element list can arrive unwrapped from PowerShell's serializer.
  const raw = row?.UrlClicks;
  const clicks = (Array.isArray(raw) ? raw : (raw ? [raw] : [])).filter(Boolean);
  return clicks
    .filter((c) => !c?.AccountUpn || !row?.RecipientAddress || norm(c.AccountUpn) === norm(row.RecipientAddress))
    .sort((a, b) => String(a?.EventTimeUTC || '').localeCompare(String(b?.EventTimeUTC || '')));
}

/** The silver id of a related-message row (its own message identity). */
export function relatedMessageSilverId(row) {
  const fromRaw = String(row?.RawLog?.id || '').trim();
  if (fromRaw) return fromRaw;
  return String(row?.EventId || '').split('|')[0].trim();
}

/**
 * ONE description helper for BOTH copies of `generateDescription`.
 *
 * The two copies hold the reveal flag with OPPOSITE polarity (`revealSubjects` in
 * TimelineView, `!maskSubjects` in eventUtils) and one of them is not exported, so the
 * seam is a shared helper taking an EXPLICIT `reveal` plus the masker as an ARGUMENT —
 * the `describeMessageTrace` / `describeExchangeItemChange` contract.
 *
 * Returns '' for anything that is not a release, so the caller falls through to its own
 * default (the `describeDeviceRegistration` contract). An UNCORRELATED release still gets
 * the generic-but-legible sentence: "released a quarantined message" is true and useful
 * where "performed QuarantineReleaseMessage" is neither.
 */
export function describeQuarantineRelease(event, reveal, maskSubjectFn) {
  if (!isQuarantineReleaseOp(event?.Operation)) return '';
  const upn = event?.UserPrincipalName || 'Unknown user';
  const sender = String(event?._releasedSender || '').trim();
  const subject = String(event?._releasedSubject || '').trim();
  const fp = !!event?._releasedFalsePositive;

  // The stamped fields are on the ROW (the v1.155.0 "fields are on the row" pattern), so
  // this helper needs no index and no context — which is what lets both copies and the
  // tests call it identically.
  let text = `${upn} released a quarantined message`;
  if (sender) text += ` from ${sender}`;
  if (subject) {
    const shown = typeof maskSubjectFn === 'function' ? maskSubjectFn(subject, reveal) : subject;
    if (shown) text += ` — '${shown}'`;
  }
  if (fp) text += ' and reported it as a false positive';
  return text;
}

/**
 * Stamp the flagged-phish release rows with what they released.
 *
 * Non-mutating: returns a new array, with fresh objects only for the rows it annotates.
 * Called at the TOP of CaseDetail's `filteredResults` memo, BEFORE any filter runs, so
 * that a release which survives the pills renders the correlated wording too — stamping
 * only the re-injected rows would have produced two different sentences for the same
 * event depending on which pill was engaged.
 */
export function stampFlaggedReleases(rawGeneralEvents, index, evidence) {
  const list = Array.isArray(rawGeneralEvents) ? rawGeneralEvents : [];
  if (!index || index.size === 0) return list;
  let touched = false;
  const out = list.map((evt) => {
    const nmid = releasedNetworkMessageId(evt);
    if (!nmid || !index.has(nmid)) return evt;
    const info = index.get(nmid);
    const ev = evidence && typeof evidence.get === 'function' ? evidence.get(nmid) : null;
    touched = true;
    return {
      ...evt,
      _flaggedPhishRelease: true,
      _releasedNetworkMessageId: nmid,
      _releasedSender: info.sender,
      _releasedSubject: info.subject,
      // OR'd across the pair, so the cmdlet record's flag also reaches the
      // quarantine-service record three seconds later.
      _releasedFalsePositive: ev ? !!ev.reportedFalsePositive : releaseReportedFalsePositive(evt),
    };
  });
  return touched ? out : list;
}

/**
 * Re-inject flagged-phish release rows that the hide pills dropped, and keep the
 * Safe-IP tab's accounting honest about it.
 *
 * 🔴 A PURE HELPER, not inline memo code, because the thing that has to be tested is
 * exactly this: that a row dropped by any of the seven hide phases comes back, that the
 * excluded-count does not then double-count it, and that an explicitly DISMISSED row is
 * still dismissed. (The `nonInteractivePersist` extraction precedent.)
 *
 * 🔴 CALLED AT THE LOOKBACK RE-INJECTION POINT — after the seven hide phases, BEFORE the
 * dismissal phase. That ordering is what makes dismissal win: an analyst who explicitly
 * dismissed the release row still gets it hidden in `hidden` mode, and `only` mode does
 * not gain a row nobody dismissed. The exception exists to defeat an IMPLICIT filter, not
 * an explicit judgment.
 */
export function stampAndReinjectFlaggedReleases({ stampedGeneral, general, safeExcluded }) {
  const stamped = Array.isArray(stampedGeneral) ? stampedGeneral : [];
  const current = Array.isArray(general) ? general : [];
  const flagged = stamped.filter((e) => e && e._flaggedPhishRelease);
  if (flagged.length === 0) {
    return { general: current, safeExcluded, reinjectedIds: new Set() };
  }
  const presentIds = new Set(current.map((e) => e && e.EventId));
  const missing = flagged.filter((e) => !presentIds.has(e.EventId));
  if (missing.length === 0) {
    return { general: current, safeExcluded, reinjectedIds: new Set() };
  }
  const reinjectedIds = new Set(missing.map((e) => e.EventId));

  // Sorting is the timeline's job (it sorts the combined stream), so append.
  const nextGeneral = [...current, ...missing];

  let nextSafeExcluded = safeExcluded;
  if (safeExcluded && Array.isArray(safeExcluded.generalEvents)) {
    const kept = safeExcluded.generalEvents.filter((e) => !reinjectedIds.has(e && e.EventId));
    const removed = safeExcluded.generalEvents.length - kept.length;
    if (removed > 0) {
      // Subtract rather than re-summing nine arrays: the count is a sum of the nine
      // per-workload lists, and re-deriving it here would be a second copy of that sum
      // that drifts the moment a workload is added to the phase above.
      nextSafeExcluded = {
        ...safeExcluded,
        generalEvents: kept,
        totalExcluded: Math.max(0, (safeExcluded.totalExcluded || 0) - removed),
      };
    }
  }

  return { general: nextGeneral, safeExcluded: nextSafeExcluded, reinjectedIds };
}
